<bn-collapsible-section
  title="Credit overview"
  icon="pi pi-credit-card"
  ariaLabel="Credit overview"
  [expanded]="false"
  persistKey="bn-credit-overview-expanded"
>
  @if (store.loading()) {
    <i
      sectionHeaderExtra
      class="pi pi-spinner pi-spin bn-credit__header-loading"
      role="status"
      aria-label="Loading credit status"
    ></i>
  } @else if (store.uiState(); as state) {
    <bn-credit-overview-state-badge sectionHeaderExtra [state]="state" />
  }

  @if (store.loading()) {
    <div class="bn-credit__loading" aria-label="Loading credit overview">
      <span></span>
      <span></span>
      <span></span>
    </div>
  } @else if (store.error()) {
    <p class="bn-credit__error" role="alert">
      We couldn't load this client's credit status.
    </p>
  } @else if (!store.sessionGuid()) {
    <p class="bn-credit__muted">Select a client to view their credit status.</p>
  } @else {
    @switch (store.uiState()) {
      @case ('not_linked') {
        <div class="bn-credit__empty">
          <h3>Credit not linked</h3>
          <p-button
            label="Send link"
            icon="pi pi-send"
            size="small"
            [loading]="sendAction.sending()"
            [disabled]="sendAction.sending()"
            (onClick)="sendToClient()"
          />
        </div>
      }
      @case ('in_progress') {
        <ng-container *ngTemplateOutlet="timeline" />
      }
      @case ('failed') {
        <ng-container *ngTemplateOutlet="timeline" />
        <div class="bn-credit__failure-action">
          <p-button
            label="Send link"
            icon="pi pi-send"
            size="small"
            [loading]="sendAction.sending()"
            [disabled]="sendAction.sending()"
            (onClick)="sendToClient()"
          />
        </div>
      }
      @case ('linked') {
        @if (store.summaryLoading()) {
          <div
            class="bn-credit__loading"
            aria-label="Loading linked credit overview"
          >
            <span></span><span></span><span></span>
          </div>
        } @else if (store.summaryError()) {
          <p class="bn-credit__error" role="alert">
            We couldn't load this client's credit overview.
          </p>
        } @else if (store.summary(); as summary) {
          @if (hasUsableScore()) {
            <section
              class="bn-credit__score-card"
              aria-label="Credit score and momentum"
            >
              <div class="bn-credit__score-header">
                <div>
                  <p class="bn-credit__eyebrow">Credit score</p>
                  <p class="bn-credit__score">
                    {{ summary.creditScore?.score }}
                  </p>
                  @if (summary.creditScore?.scoreRange; as range) {
                    <span class="bn-credit__score-range">{{ range }}</span>
                  }
                </div>
                <div class="bn-credit__report-action">
                  @if (summary.creditScore?.date; as updatedDate) {
                    <span>Updated {{ updatedDate }}</span>
                  }
                  <p-button
                    label="View credit report"
                    icon="pi pi-external-link"
                    iconPos="right"
                    severity="secondary"
                    [outlined]="true"
                    size="small"
                    [loading]="store.reportLoading()"
                    [disabled]="store.reportLoading()"
                    (onClick)="openReport()"
                  />
                </div>
              </div>

              @if (scoreHistory().length > 1) {
                <div class="bn-credit__momentum">
                  <h3>
                    <i class="pi pi-chart-line" aria-hidden="true"></i>
                    Credit Momentum
                  </h3>
                  <div class="bn-credit__chart">
                    <div class="bn-credit__chart-labels" aria-hidden="true">
                      @for (label of momentumAxisLabels(); track label) {
                        <span>{{ label }}</span>
                      }
                    </div>
                    <div class="bn-credit__chart-plot">
                      <svg
                        viewBox="0 0 900 120"
                        preserveAspectRatio="none"
                        role="img"
                        [attr.aria-label]="momentumAccessibleLabel()"
                      >
                        <line x1="0" y1="0" x2="900" y2="0" />
                        <line x1="0" y1="60" x2="900" y2="60" />
                        <line x1="0" y1="120" x2="900" y2="120" />
                        <polyline [attr.points]="momentumPoints()" />
                      </svg>
                      <div class="bn-credit__chart-dates">
                        <span>{{ firstHistoryLabel() }}</span>
                        <span>{{ lastHistoryLabel() }}</span>
                      </div>
                    </div>
                  </div>
                </div>
              }
              @if (summary.creditScore?.scoreHistoryIssueCount) {
                <p class="bn-credit__data-warning" role="status">
                  Some credit history details are unavailable.
                </p>
              }
            </section>
          } @else {
            <section
              class="bn-credit__no-score-card"
              aria-label="Credit score unavailable"
            >
              <span class="bn-credit__no-score-icon" aria-hidden="true">
                <i class="pi pi-exclamation-triangle"></i>
              </span>
              <div class="bn-credit__no-score-copy">
                <h3>Credit score unavailable</h3>
                <p>The linked credit report is unable to generate a score.</p>
              </div>
              @if (reportAvailable()) {
                <p-button
                  label="View credit report"
                  icon="pi pi-external-link"
                  iconPos="right"
                  severity="secondary"
                  [outlined]="true"
                  size="small"
                  [loading]="store.reportLoading()"
                  [disabled]="store.reportLoading()"
                  (onClick)="openReport()"
                />
              }
            </section>
          }

          @if (reportAvailable()) {
            @if (store.reportError()) {
              <p class="bn-credit__error" role="alert">
                We couldn't load this client's credit report.
              </p>
            }

            <bn-collapsible-section
              title="Liabilities & Debt Profile"
              icon="pi pi-clone"
              ariaLabel="Liabilities and debt profile"
              persistKey="bn.finances.credit-overview.liabilities.expanded"
            >
              <div class="bn-credit__debt-summary">
                <div>
                  <p>Total debt</p>
                  <strong>
                    {{ totalDebt() | currency: 'USD' : 'symbol' : '1.0-0' }}
                  </strong>
                </div>
                <div>
                  <p>Accounts</p>
                  <strong>{{ summary.debt.summary.totalDebtAccounts }}</strong>
                </div>
              </div>

              @if (debtAggregation().length) {
                <section
                  class="bn-credit__debt-breakdown"
                  aria-labelledby="bn-credit-debt-breakdown-title"
                >
                  <h3 id="bn-credit-debt-breakdown-title">Debt breakdown</h3>
                  <div
                    class="bn-credit__debt-bar"
                    role="img"
                    [attr.aria-label]="debtAggregationLabel()"
                  >
                    @for (item of debtAggregation(); track item.type) {
                      <span
                        class="bn-credit__debt-segment"
                        [attr.data-type]="item.type"
                        [style.width.%]="item.percentage"
                      ></span>
                    }
                  </div>
                  <ul class="bn-credit__debt-legend">
                    @for (item of debtAggregation(); track item.type) {
                      <li>
                        <span
                          class="bn-credit__debt-swatch"
                          [attr.data-type]="item.type"
                          aria-hidden="true"
                        ></span>
                        <span>{{ item.label }}</span>
                        <strong>{{ item.percentage }}%</strong>
                      </li>
                    }
                  </ul>
                </section>
              }
              @if (summary.debt.summary.aggregationIssueCount) {
                <p class="bn-credit__data-warning" role="status">
                  Some debt breakdown details are unavailable.
                </p>
              }

              <bds-scroll-region
                orientation="horizontal"
                styleClass="bn-credit__table-wrap"
                ariaLabel="Liabilities table; scroll horizontally to view all columns"
              >
                <p-table
                  [value]="visibleAccounts()"
                  [customSort]="true"
                  [sortField]="sortColumn()"
                  [sortOrder]="sortOrder()"
                  (sortFunction)="onTableSort($event)"
                  styleClass="bn-credit__table"
                  [tableStyle]="{ 'min-width': '58rem' }"
                >
                  <ng-template pTemplate="header">
                    <tr>
                      <th pSortableColumn="lender" scope="col">
                        <span class="bn-credit__sort-header">
                          Lender
                          <p-sortIcon field="lender" />
                        </span>
                      </th>
                      <th pSortableColumn="type" scope="col">
                        <span class="bn-credit__sort-header">
                          Type
                          <p-sortIcon field="type" />
                        </span>
                      </th>
                      <th pSortableColumn="balance" scope="col">
                        <span class="bn-credit__sort-header">
                          Balance
                          <p-sortIcon field="balance" />
                        </span>
                      </th>
                      <th pSortableColumn="creditLimit" scope="col">
                        <span class="bn-credit__sort-header">
                          Credit limit
                          <p-sortIcon field="creditLimit" />
                        </span>
                      </th>
                      <th pSortableColumn="utilizationRate" scope="col">
                        <span class="bn-credit__sort-header">
                          Utilization
                          <p-sortIcon field="utilizationRate" />
                        </span>
                      </th>
                      <th pSortableColumn="minPayment" scope="col">
                        <span class="bn-credit__sort-header">
                          Min payment
                          <p-sortIcon field="minPayment" />
                        </span>
                      </th>
                      <th pSortableColumn="originDate" scope="col">
                        <span class="bn-credit__sort-header">
                          Origin date
                          <p-sortIcon field="originDate" />
                        </span>
                      </th>
                      <th pSortableColumn="status" scope="col">
                        <span class="bn-credit__sort-header">
                          Status
                          <p-sortIcon field="status" />
                        </span>
                      </th>
                    </tr>
                  </ng-template>
                  <ng-template pTemplate="body" let-account>
                    <tr>
                      <td>
                        <strong>
                          {{ account.lender ?? 'Account details unavailable' }}
                        </strong>
                        @if (account.hasDataIssue) {
                          <span class="bn-credit__row-warning">
                            Some details unavailable
                          </span>
                        }
                      </td>
                      <td>{{ account.type ?? '—' }}</td>
                      <td>
                        @if (account.balance === null) {
                          —
                        } @else {
                          <strong>
                            {{
                              account.balance
                                | currency: 'USD' : 'symbol' : '1.0-0'
                            }}
                          </strong>
                        }
                      </td>
                      <td>
                        @if (account.creditLimit === null) {
                          —
                        } @else {
                          {{
                            account.creditLimit
                              | currency: 'USD' : 'symbol' : '1.0-0'
                          }}
                        }
                      </td>
                      <td>
                        {{
                          account.creditLimit === null ||
                          account.utilizationRate === null
                            ? '—'
                            : account.utilizationRate + '%'
                        }}
                      </td>
                      <td>
                        @if (account.minPayment === null) {
                          —
                        } @else {
                          {{
                            account.minPayment
                              | currency: 'USD' : 'symbol' : '1.0-0'
                          }}/mo
                        }
                      </td>
                      <td>
                        {{
                          account.originDate
                            ? formatOriginDate(account.originDate)
                            : '—'
                        }}
                      </td>
                      <td>{{ account.status ?? '—' }}</td>
                    </tr>
                  </ng-template>
                  </p-table>
              </bds-scroll-region>
              <footer class="bn-credit__pagination">
                <span>
                  Showing {{ pageStart() }}–{{ pageEnd() }} of
                  {{ sortedAccounts().length }}
                </span>
                <div>
                  <button
                    type="button"
                    aria-label="Previous liabilities page"
                    [disabled]="page() === 0"
                    (click)="previousPage()"
                  >
                    <i class="pi pi-angle-left" aria-hidden="true"></i>
                  </button>
                  <strong>Page {{ page() + 1 }} of {{ pageCount() }}</strong>
                  <button
                    type="button"
                    aria-label="Next liabilities page"
                    [disabled]="page() + 1 >= pageCount()"
                    (click)="nextPage()"
                  >
                    <i class="pi pi-angle-right" aria-hidden="true"></i>
                  </button>
                </div>
              </footer>
            </bn-collapsible-section>
          }
        }
      }
    }
  }
</bn-collapsible-section>

<ng-template #timeline>
  <div class="bn-credit__timeline-panel">
    <header class="bn-credit__timeline-header">
      <h3>
        <i class="pi pi-wave-pulse" aria-hidden="true"></i> Linking status
      </h3>
      <span
        class="bn-credit__timeline-status"
        [attr.data-state]="store.uiState()"
      >
        {{ store.status() }}
      </span>
    </header>
    <ol class="bn-credit__timeline" aria-label="Credit linking progress">
      @for (step of store.timeline(); track step.id) {
        <li class="bn-credit__timeline-step" [attr.data-state]="step.state">
          <span class="bn-credit__timeline-marker" aria-hidden="true">
            @if (step.state === 'complete') {
              <i class="pi pi-check"></i>
            } @else if (step.state === 'current') {
              <i class="pi pi-asterisk"></i>
            } @else if (step.state === 'failed') {
              <i class="pi pi-times"></i>
            }
          </span>
          <div>
            <div class="bn-credit__timeline-title-row">
              <p class="bn-credit__timeline-title">{{ step.title }}</p>
            </div>
            @if (step.subtext) {
              <p class="bn-credit__timeline-subtext">{{ step.subtext }}</p>
            }
          </div>
        </li>
      }
    </ol>
  </div>
</ng-template>
